import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callBackend } from '../utils/api';
import { isValidPosSizingPublicationBase } from '../utils/posSizingPublicationContract';
import {
    POSITION_SIZING_ATTRIBUTE_RECALCULATION,
    POSITION_SIZING_ATTRIBUTE_SETTING_MUTATION,
    POSITION_SIZING_ATTRIBUTE_SWITCH,
    attributeDefinitionIdFromOption,
    attributeOptionKey,
    buildPositionSizingSummaryRows,
    buildPositionSizingTargetRows,
    defaultSummaryOptionKey,
    groupLocatorKey,
    positionSizingAttributeOptions,
    positionSizingAttributeDefaults,
    requirePositionSizingAttributeCalculation,
    requirePositionSizingAttributeCapability,
    requirePositionSizingAttributeSettingMutation,
    requirePositionSizingAttributeState,
    targetOptionByKey,
} from '../utils/positionSizingAttributeContract';

const SETTING_ENDPOINT = 'update_position_sizing_attribute_target_setting_v2';
const SWITCH_ENDPOINT = 'switch_position_sizing_attribute_target_v2';
const RECALCULATE_ENDPOINT = 'recalculate_position_sizing_attribute_target_v2';
const SUMMARY_STORAGE_PREFIX = 'pos_sizing.summary_attribute.v2';

function errorMessage(error) {
    const raw = error instanceof Error ? error.message : String(error || '');
    const payloadStart = raw.indexOf('{');
    if (payloadStart >= 0) {
        try {
            const payload = JSON.parse(raw.slice(payloadStart));
            const detail = payload?.detail;
            if (typeof detail === 'string' && detail.trim()) return detail;
            if (typeof detail?.message === 'string' && detail.message.trim()) {
                return detail.message;
            }
        } catch {
            // Keep the privacy-safe generic message below.
        }
    }
    return 'Unable to update generic Position Sizing. Refresh and try again.';
}

function summaryStorageKey(portfolioId) {
    return `${SUMMARY_STORAGE_PREFIX}:${portfolioId}`;
}

export function readSavedSummaryOption(portfolioId) {
    if (typeof window === 'undefined' || !window.localStorage || !portfolioId) return '';
    try {
        return String(window.localStorage.getItem(summaryStorageKey(portfolioId)) || '');
    } catch {
        return '';
    }
}

export function saveSummaryOption(portfolioId, optionKey) {
    if (typeof window === 'undefined' || !window.localStorage || !portfolioId) return;
    try {
        window.localStorage.setItem(summaryStorageKey(portfolioId), optionKey);
    } catch {
        // Browser storage is optional; the server-owned calculation remains valid.
    }
}

export default function usePositionSizingAttributes({
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    hydratedWorkspace = null,
    workspaceBusy = false,
    active = true,
    onPublished = null,
    onTargetRecalculationStateChange = null,
}) {
    const [capabilityLoading, setCapabilityLoading] = useState(true);
    const [attributeState, setAttributeState] = useState(null);
    const [calculation, setCalculation] = useState(null);
    const [calculationAdmitted, setCalculationAdmitted] = useState(false);
    const [viewTargetKey, setViewTargetKey] = useState('');
    const [summaryKey, setSummaryKey] = useState('');
    const [savingSettingKey, setSavingSettingKey] = useState('');
    const [publishing, setPublishing] = useState(false);
    const [error, setError] = useState('');
    const requestGenerationRef = useRef(0);
    const publishingRef = useRef(false);
    const savingSettingRef = useRef(false);
    const installedHydrationKeyRef = useRef('');

    const defaults = useMemo(() => positionSizingAttributeDefaults({
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
    }), [defaultScaler, defaultTargetVolDollar, defaultTargetVolPct]);

    const installWorkspace = useCallback((stateValue, calculationValue) => {
        const validatedState = requirePositionSizingAttributeState(stateValue);
        const state = {
            ...validatedState,
            targets: positionSizingAttributeOptions(validatedState),
        };
        const result = calculationValue == null
            ? null
            : requirePositionSizingAttributeCalculation(calculationValue);
        const activeKey = attributeOptionKey(state.target_attribute_definition_id);
        const attributeTargets = state.targets;
        const activeTargetAvailable = attributeTargets.some(
            (target) => attributeOptionKey(target.attribute_definition_id) === activeKey,
        );
        const defaultTargetKey = activeTargetAvailable
            ? activeKey
            : attributeTargets.length > 0
                ? attributeOptionKey(attributeTargets[0].attribute_definition_id)
                : '';
        const storedSummaryKey = readSavedSummaryOption(state.portfolio_id);
        const selectedSummaryKey = defaultSummaryOptionKey(
            state,
            storedSummaryKey,
        );
        setAttributeState(state);
        setCalculation(result);
        setViewTargetKey((current) => (
            targetOptionByKey(state, current)?.attribute_definition_id != null
                ? current
                : defaultTargetKey
        ));
        setSummaryKey((current) => defaultSummaryOptionKey(
            state,
            current || selectedSummaryKey,
        ));
        setError('');
    }, []);

    useEffect(() => {
        if (!active) return;
        if (
            hydratedWorkspace?.key
            && installedHydrationKeyRef.current === hydratedWorkspace.key
        ) return;
        const generation = requestGenerationRef.current + 1;
        requestGenerationRef.current = generation;
        if (!hydratedWorkspace?.key) {
            installedHydrationKeyRef.current = '';
            setCapabilityLoading(Boolean(workspaceBusy));
            setAttributeState(null);
            setCalculation(null);
            setCalculationAdmitted(false);
            setError('');
            return;
        }
        try {
            requirePositionSizingAttributeCapability(
                hydratedWorkspace.capability,
            );
            const state = requirePositionSizingAttributeState(hydratedWorkspace.state);
            const result = requirePositionSizingAttributeCalculation(
                hydratedWorkspace.calculation,
            );
            setCalculationAdmitted(state.calculation_admission.can_calculate);
            installWorkspace(state, result);
            installedHydrationKeyRef.current = hydratedWorkspace.key;
        } catch (hydrationError) {
            installedHydrationKeyRef.current = '';
            setAttributeState(null);
            setCalculation(null);
            setCalculationAdmitted(false);
            setError(errorMessage(hydrationError));
        } finally {
            setCapabilityLoading(false);
        }
    }, [
        active,
        hydratedWorkspace?.calculation,
        hydratedWorkspace?.capability,
        hydratedWorkspace?.key,
        hydratedWorkspace?.state,
        installWorkspace,
        workspaceBusy,
    ]);

    useEffect(() => () => {
        requestGenerationRef.current += 1;
    }, []);

    const selectSummary = useCallback((nextKey) => {
        if (targetOptionByKey(attributeState, nextKey)?.attribute_definition_id == null) return;
        setSummaryKey(nextKey);
        saveSummaryOption(attributeState?.portfolio_id, nextKey);
    }, [attributeState]);

    const installPublishedCalculation = useCallback((rawResult) => {
        const result = requirePositionSizingAttributeCalculation(rawResult);
        if (!isValidPosSizingPublicationBase(rawResult?.publication_base)) {
            throw new Error('Generic Position Sizing publication evidence is invalid.');
        }
        setCalculation(result);
        setAttributeState((current) => current ? {
            ...current,
            target_kind: result.target.target_kind,
            target_attribute_definition_id:
                result.target.target_attribute_definition_id,
            target_version: result.target.target_version,
        } : current);
        onPublished?.(rawResult);
        setError('');
        return result;
    }, [onPublished]);

    const saveSetting = useCallback(async (updatedRow, priorRow) => {
        if (!calculationAdmitted) {
            throw new Error('Sync Draft before changing Position Sizing attribute settings.');
        }
        if (savingSettingRef.current) {
            throw new Error('A Position Sizing setting is already saving.');
        }
        const locatorKey = groupLocatorKey(priorRow?.group);
        if (!locatorKey || locatorKey !== groupLocatorKey(updatedRow?.group)) {
            throw new Error('Position Sizing category identity changed. Refresh and try again.');
        }
        savingSettingRef.current = true;
        setSavingSettingKey(locatorKey);
        setError('');
        try {
            const response = requirePositionSizingAttributeSettingMutation(
                await callBackend(SETTING_ENDPOINT, 'POST', {
                    contract: POSITION_SIZING_ATTRIBUTE_SETTING_MUTATION,
                    group: priorRow.group,
                    target_vol_pct: Number(updatedRow.target_vol_pct),
                    target_vol_dollar: Number(updatedRow.target_vol_dollar),
                    scaler: Number(updatedRow.scaler),
                    expected_version: priorRow.version,
                }),
            );
            const saved = response.setting;
            setAttributeState((current) => {
                if (!current) return current;
                return {
                    ...current,
                    targets: current.targets.map((target) => ({
                        ...target,
                        group_settings: target.group_settings.map((setting) => (
                            groupLocatorKey(setting.group) === locatorKey
                                ? saved
                                : setting
                        )),
                    })),
                };
            });
            const activeKey = attributeOptionKey(
                attributeState?.target_attribute_definition_id,
            );
            const switchingTarget = viewTargetKey !== activeKey;
            const published = await callBackend(
                switchingTarget ? SWITCH_ENDPOINT : RECALCULATE_ENDPOINT,
                'POST',
                switchingTarget
                    ? {
                        contract: POSITION_SIZING_ATTRIBUTE_SWITCH,
                        ...defaults,
                        target_attribute_definition_id:
                            attributeDefinitionIdFromOption(viewTargetKey),
                        expected_target_version: attributeState.target_version,
                    }
                    : {
                        contract: POSITION_SIZING_ATTRIBUTE_RECALCULATION,
                        ...defaults,
                    },
            );
            installPublishedCalculation(published);
            return { ...updatedRow, ...saved, id: locatorKey };
        } catch (settingError) {
            const message = errorMessage(settingError);
            setError(message);
            throw new Error(message);
        } finally {
            savingSettingRef.current = false;
            setSavingSettingKey('');
        }
    }, [
        attributeState,
        calculationAdmitted,
        defaults,
        installPublishedCalculation,
        viewTargetKey,
    ]);

    const selectTarget = useCallback(async (nextKey) => {
        const option = targetOptionByKey(attributeState, nextKey);
        if (option?.attribute_definition_id == null || publishingRef.current) return null;
        const priorKey = attributeOptionKey(
            attributeState.target_attribute_definition_id,
        );
        setViewTargetKey(nextKey);
        if (!calculationAdmitted || option.group_settings.length === 0) return null;
        if (nextKey === priorKey) return calculation;
        const generation = requestGenerationRef.current + 1;
        requestGenerationRef.current = generation;
        publishingRef.current = true;
        setPublishing(true);
        onTargetRecalculationStateChange?.(true);
        setError('');
        try {
            const result = await callBackend(SWITCH_ENDPOINT, 'POST', {
                contract: POSITION_SIZING_ATTRIBUTE_SWITCH,
                ...defaults,
                target_attribute_definition_id: attributeDefinitionIdFromOption(
                    nextKey,
                ),
                expected_target_version: attributeState.target_version,
            });
            if (requestGenerationRef.current !== generation) return null;
            return installPublishedCalculation(result);
        } catch (publishError) {
            if (requestGenerationRef.current !== generation) return null;
            setViewTargetKey(priorKey);
            setError(errorMessage(publishError));
            return null;
        } finally {
            publishingRef.current = false;
            setPublishing(false);
            onTargetRecalculationStateChange?.(false);
        }
    }, [
        attributeState,
        calculation,
        calculationAdmitted,
        defaults,
        installPublishedCalculation,
        onTargetRecalculationStateChange,
    ]);

    const summaryOptions = attributeState?.targets ?? [];
    const selectedSummary = targetOptionByKey(attributeState, summaryKey);
    const targetRows = useMemo(() => buildPositionSizingTargetRows(
        attributeState,
        calculation,
        viewTargetKey,
    ), [attributeState, calculation, viewTargetKey]);
    const summaryRows = useMemo(() => buildPositionSizingSummaryRows(
        attributeState,
        calculation,
        summaryKey,
    ), [attributeState, calculation, summaryKey]);

    return {
        capabilityLoading,
        publishing,
        error,
        clearError: () => setError(''),
        attributeState,
        calculation,
        calculationAdmitted,
        hasAttributeOptions: Boolean(attributeState?.targets?.length),
        viewTargetKey,
        selectTarget,
        summaryKey,
        selectSummary,
        selectedSummary,
        summaryOptions,
        targetRows,
        summaryRows,
        savingSettingKey,
        saveSetting,
    };
}
